import { useField, useForm, useIsFormDirty } from 'vee-validate'
import { createGlobalState } from '@vueuse/core'

import type { FieldMeta } from 'vee-validate'
import type { PredictionParameters } from '~/types'

import { useMatterInfo } from '~/modules/matterInfo'
import { predictionParamsDefaults, predictionParamsKeys } from '~/utils'

import validationSchema from '~/schemas/validators'
import warningsSchema from '~/schemas/warnings'

export function getWarnings<T extends PredictionParameters>(params: T, metas: Record<keyof T, FieldMeta<unknown>>, errors: Partial<Record<keyof T, string>>) {
  return Object.entries(params).map(([key, value]) => {
    if (metas[key as keyof T].dirty && metas[key as keyof T].valid && !errors[key as keyof T]) {
      const schema = warningsSchema[key as keyof PredictionParameters]

      if (!schema)
        return [key, false] as const

      return [key, schema(value as never)] as const
    }
    else { return [key, false] as const }
  })
    .filter(([_, val]) => val !== false)
    .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {} as Partial<Record<keyof PredictionParameters, string | undefined>>)
}

export const usePredictionParams = createGlobalState(() => {
  const { initialPredictionParameters } = useMatterInfo()

  const emptyValues = predictionParamsKeys.reduce((acc, key) => ({ ...acc, [key]: undefined }), {}) as Record<keyof PredictionParameters, undefined>
  const nullValues = predictionParamsKeys.reduce((acc, key) => ({ ...acc, [key]: null }), {}) as Required<PredictionParameters>

  const { values: predictionParameters, handleSubmit, setValues, isSubmitting, resetForm: handleReset, submitForm, errors, setFieldValue } = useForm<PredictionParameters>({
    validationSchema,
    initialValues: emptyValues,
    validateOnMount: true,
  })

  const fieldMetas = (Object.keys(predictionParameters) as (keyof PredictionParameters)[]).reduce((acc, key) => ({ ...acc, [key]: useField(key).meta }), {} as Record<keyof PredictionParameters, FieldMeta<unknown>>)

  const isFormDirty = useIsFormDirty()

  const warnings = computed(() => getWarnings(predictionParameters, fieldMetas, errors.value))

  const resetForm = () => {
    const filteredInitialPredictionParameters = initialPredictionParameters.value ? Object.entries(initialPredictionParameters.value).filter(([, value]) => value !== undefined).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as PredictionParameters) : undefined
    const values = { ...nullValues, ...predictionParamsDefaults, ...filteredInitialPredictionParameters }

    handleReset({ values })
  }

  watch(initialPredictionParameters, (val) => {
    if (!val)
      return
    resetForm()
  })

  return {
    predictionParameters,
    initialPredictionParameters,
    isFormDirty,
    handleSubmit,
    isSubmitting,
    errors,
    warnings,
    resetForm,
    setValues,
    setFieldValue,
    submitForm,
  }
})
